import { css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import dataOffer from './offers.js';
import assetsPath from '../../index.js';

interface OfferData {
  offer: string;
  children: (Record<string, string> | Record<string, boolean>)[];
}

/**
 * Nordnet Horizontal Subvention Block atom. Used standalone.
 *
 * Offer : The offer. (required, i.e. SAT_PRO, 4G_FIX, SAT)
 */
@customElement('nn-horizontal-subvention-block')
export class horizontalSubventionBlock extends LitElement {
  static styles = css`
    .modal-subvention-block * {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }
    .modal-subvention-block {
      background: var(--nn-white, #fff);
    }
    .modal-subvention-block .subtitle-02 {
      color: var(--nn-black, #000);
      font-family: 'font-heavy';
      font-size: 24px;
      line-height: calc(2ex + 4px);
    }
    .modal-subvention-block .heading-01 {
      align-items: center;
      color: var(--nn-black, #000);
      display: flex;
      font-family: 'font-heavy';
      font-size: 48px;
      gap: 16px;
      justify-content: flex-start;
      line-height: calc(4px + 2ex);
    }
    .modal-subvention-block .heading-01 small {
      color: var(--nn-grey-54, #686868);
      font-size: 28px;
      line-height: 1;
    }
    .modal-subvention-block .heading-04 {
      color: var(--nn-black, #000);
      font-family: 'font-light';
      font-size: 24px;
      line-height: calc(4px + 2ex);
    }
    .modal-subvention-block .subtitle-01 {
      color: var(--nn-black, #000);
      font-family: 'font-heavy';
      font-size: 24px;
      line-height: calc(2ex + 4px);
      padding: 20px 0;
    }
    .modal-subvention-block .subtitle-02 {
      color: var(--nn-black, #000);
      font-family: 'font-heavy';
      font-size: 20px;
      line-height: calc(2ex + 4px);
      padding: 0 0 8px;
    }
    .modal-subvention-block .paragraph-01 {
      color: var(--nn-grey-54, #686868);
      font-family: 'font-light';
      font-size: 20px;
      line-height: calc(2ex + 4px);
    }
    .modal-subvention-block .paragraph-01 + .first {
      margin-top: 16px;
    }
    .modal-subvention-block * b {
      font-family: 'font-heavy';
      font-weight: normal;
    }
    .modal-subvention-block .paragraph-01 b {
      color: var(--nn-black, #000);
    }
    .modal-subvention-block .generic-block {
      border: 1px solid var(--brand-gray-20, #efefef);
      border-radius: 20px;
      background: var(--brand-gray-5, #fbfbfb);
      margin: 0 0 24px;
      padding: 24px 36px;
    }
    .modal-subvention-block .custom-flex {
      align-items: stretch;
      display: flex;
      flex-wrap: wrap;
      gap: 16px;
      justify-content: space-between;
    }
    .modal-subvention-block .custom-flex--column {
      flex-basis: auto;
      flex-shrink: 0;
      width: calc(50% - 8px);
    }
    .modal-subvention-block .mentions {
      color: var(--nn-grey-54, #686868);
      font-family: 'font-light';
      font-size: 16px;
      line-height: calc(2ex + 4px);
      width: 100%;
    }
    .modal-subvention-block .first .custom-flex--column {
      width: 100%;
    }
    @media (max-width: 767px) {
      .modal-subvention-block .custom-flex--column {
        align-items: center;
        display: inline-flex;
        flex-wrap: wrap;
        justify-content: center;
        text-align: center;
        width: 100%;
      }
      .modal-subvention-block .second .custom-flex--column:first-child {
        margin: 12px 0 8px;
      }
      .modal-subvention-block .subtitle-02 {
        text-align: center;
      }
      .modal-subvention-block .heading-01 {
        font-size: 36px;
        gap: 8px;
        text-align: center;
      }
      .modal-subvention-block .heading-01 small {
        font-size: 28px;
      }
      .modal-subvention-block .heading-04 {
        flex-basis: auto;
        flex-shrink: 0;
        width: 100%;
      }
      .modal-subvention-block .generic-block {
        border-radius: 16px;
        padding: 16px;
      }
    }
    @media (max-width: 479px) {
      .modal-subvention-block .heading-01 {
        font-size: 30px;
        text-align: center;
      }
      .modal-subvention-block .heading-01 small {
        font-size: 20px;
      }
    }
  `;

  @property({ type: String }) offer = 'SAT';

  render() {
    const offers = JSON.parse(
      JSON.stringify(dataOffer).replace(/<prefixPath>/g, assetsPath)
    );
    const selectedOffer = offers.find(
      (offer: OfferData) => offer.offer === this.offer
    );
    return html`
      <div class="modal-subvention-block ${selectedOffer}">
        <div
          class="paragraph-01"
          .innerHTML="${selectedOffer.children[0].paragraph}"
        ></div>
        <!--   check if subtitle exists so show subtitle-01     -->
        ${this.renderSubtitle(selectedOffer)}
        <div class="generic-block first">
          <div
            class="subtitle-02"
            .innerHTML="${selectedOffer.children[0].block01Title}"
          ></div>
          <div class="custom-flex">
            <div
              class="custom-flex--column"
              .innerHTML="${selectedOffer.children[0].block01column01Text}"
            ></div>
            <div
              class="custom-flex--column mentions"
              .innerHTML="${selectedOffer.children[0].block01columnMentions}"
            ></div>
          </div>
        </div>
        <!--   check if black02 is true so show second     -->
        ${this.renderSecondBlock(selectedOffer)}
      </div>
    `;
  }

  renderSubtitle = (selectedOffer: OfferData) => {
    const subTitle = selectedOffer.children[0].subtitle;
    if (subTitle) {
      return html`
        <div
          class="subtitle-01"
          .innerHTML="${selectedOffer.children[0].subtitle}"
        ></div>
      `;
    }
    return '';
  };

  renderSecondBlock = (selectedOffer: OfferData) => {
    const secondBlock = selectedOffer.children[0].block02;
    if (secondBlock === true) {
      return html`
        <div class="generic-block second">
          <div
            class="subtitle-02"
            .innerHTML="${selectedOffer.children[0].block02Title}"
          ></div>
          <div class="custom-flex">
            <div
              class="custom-flex--column"
              .innerHTML="${selectedOffer.children[0].block02column01Text}"
            ></div>
            <div
              class="custom-flex--column"
              .innerHTML="${selectedOffer.children[0].block02column02Text}"
            ></div>
            <div
              class="custom-flex--column mentions"
              .innerHTML="${selectedOffer.children[0].block02columnMentions}"
            ></div>
          </div>
        </div>
      `;
    }
    return '';
  };

  parseHtml = (str?: any) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(str, 'text/html');
    return doc.body;
  };
}
